
import { Capacitor } from '@capacitor/core';
import { mapGetters, mapMutations, mapActions } from 'vuex';

export default {
    components: {
        RestaurantGhost: () => import('~/components/RestaurantGhost.vue'),
        RestaurantMenu: () => import('~/components/RestaurantMenu.vue'),
        CartDrawer: () => import('~/components/CartDrawer.vue'),
        Back: () => import('~/components/Back.vue'),
    },
    head() {
        return {
            title: this.seoTitle,
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: this.seoDescription,
                },
                {
                    hid: 'og:title',
                    name: 'og:title',
                    content: this.seoTitle,
                },
                {
                    hid: 'og:description',
                    name: 'og:description',
                    content: this.seoDescription,
                },
                {
                    hid: 'og:image',
                    name: 'og:image',
                    content: this.seoOgImage,
                },
            ],
        };
    },
    transition(to, from) {
        if (from && to) {
            if (from.name.includes('happy-') || from.name === 'index' || from.name === 'supplier-chain-slug') {
                return 'page';
            }
            return 'page-reverse';
        }
        return 'page';
    },
    async fetch() {
        try {
            const data = await this.getRestaurant();
            this.restaurantLocation = data;
        } catch (error) {
            this.$bugsnag.notify(new Error(error));
            console.log({ error });
        }
    },
    data() {
        return {
            restaurantLocation: null,
            showVatChooser: true,
        };
    },
    computed: {
        ...mapGetters(['loggedInUser', 'vat']),
        ...mapGetters('location', ['location']),
        ...mapGetters('sheet', ['type']),
        ...mapGetters('toast', ['msg']),
        ...mapGetters('order', ['deliveryMethod', 'isDelivery', 'address', 'coupon']),
        ...mapGetters('sideDrawer', ['sideDrawerType', 'sideDrawerData']),
        isNative() {
            return Capacitor.isNative;
        },
        slug() {
            return this.$route.params.slug;
        },
        name() {
            return this.restaurantLocation && this.restaurantLocation.name;
        },
        distance() {
            return this.restaurantLocation && this.restaurantLocation.distance;
        },
        restaurant() {
            return this.restaurantLocation && this.restaurantLocation.restaurant;
        },
        menu() {
            return this.restaurantLocation && this.restaurantLocation.menu;
        },
        showTestDataLayer() {
            if (process.browser) {
                if (window) {
                    if (window.location.href.includes('dataLayerTest')) {
                        return true;
                    }
                }
            }
            return false;
        },
        seoTitle() {
            if (this.restaurantLocation && this.restaurantLocation.seo_title !== '') {
                return `${this.restaurantLocation?.seo_title}`;
            }
            if (this.restaurant && this.restaurantLocation) {
                return `${this.restaurant?.name} - ${this.restaurantLocation?.name}`;
            }
            return 'TRIBE';
        },
        seoDescription() {
            if (this.restaurantLocation && this.restaurantLocation.seo_title !== '') {
                return `${this.restaurantLocation?.seo_description}`;
            }
            return 'Lækker mad har aldrig været lettere at finde, lad os inspirere dig';
        },
        seoOgImage() {
            if (this.restaurant) {
                return this.restaurant.portrait;
            }
            return '/tribe-og-image.png';
        },
        groupOrderEnabled() {
            return this.restaurantLocation && this.restaurantLocation.grouporder_enabled;
        },
        marketplace() {
            let marketplace = null;
            if (typeof window !== 'undefined') {
                marketplace = localStorage.getItem('marketplace');
            }
            return marketplace;
        },
        isWhiteLabelWithMultibleLocations() {
            return !this.marketplace || (this.marketplace && this.restaurant.locations.length > 1);
        },
    },
    watch: {
        location: {
            async handler() {
                if (!this._inactive) {
                    const data = await this.getRestaurant();
                    this.restaurantLocation = data;
                }
            },
            deep: true,
        },
        address: {
            async handler(newVal, oldVal) {
                if (!this._inactive && oldVal) {
                    await this.ghostDelivery(true);
                    const data = await this.getRestaurant();
                    this.restaurantLocation = data;
                    if (this.isDelivery) {
                        const optimalRestaurant = await this.restaurantLocationChain();
                        if (optimalRestaurant && optimalRestaurant.id !== data.id) {
                            this.$router.push(`/supplier/${optimalRestaurant.slug}`);
                        } else {
                            this.ghostDelivery(false);
                        }
                    }
                }
            },
            deep: true,
            immediate: true,
        },
        slug: {
            async handler() {
                if (!this._inactive && this.isDelivery) {
                    const data = await this.getRestaurant();
                    setTimeout(() => {
                        this.ghostDelivery(false);
                    }, 200);
                    this.restaurantLocation = data;
                }
            },
        },
        restaurantLocation: {
            handler(restaurantLocation) {
                // if (this.msg) this.closeToast();
                this.setRestaurantLocation(restaurantLocation);
                const optimalRestaurant = this.restaurantLocationChain();
                if (
                    this.isDelivery &&
                    (!optimalRestaurant || (optimalRestaurant && optimalRestaurant.id === restaurantLocation.id)) &&
                    (!restaurantLocation.delivery_option || !restaurantLocation.delivery_option.inside_radius)
                ) {
                    this.setToast({ msg: 'Restauranten leverer ikke til den valgte adresse' });
                }
                if (this.type === 'RestaurantLocation') {
                    this.setSheet({
                        type: 'RestaurantLocation',
                        data: { heading: 'Vælg en anden lokation', locations: restaurantLocation.restaurant.locations },
                    });
                }
            },
            deep: true,
        },
    },
    async activated() {
        // Call fetch again if last fetch more than 15 min ago
        /* if (this.$fetchState.timestamp <= Date.now() - 1000 * 60 * 15) {
            this.$fetch();
        } */
        await this.removeCoupon();
    },
    methods: {
        ...mapMutations('sideDrawer', ['setSideDrawer', 'closeSideDrawer']),
        ...mapMutations('sheet', ['setSheet']),
        ...mapMutations('order', ['setRestaurantLocation', 'removeCoupon']),
        ...mapMutations('toast', ['closeToast']),
        ...mapMutations('ghostState', ['ghostDelivery']),
        ...mapMutations(['setVat']),
        ...mapActions('toast', ['setToast']),
        async doSetVat(type) {
            this.showVatChooser = false;
            await this.setVat(type);
            location.reload();
        },
        openCart() {
            this.setSideDrawer({ type: 'Cart' });
        },
        closeCart() {
            this.closeSideDrawer();
        },
        async getRestaurant() {
            const options = {
                params: {
                    include: 'restaurant.locations,restaurant.categories,menu.categories.foods',
                },
            };
            if (this.deliveryMethod === 'pickup' && this.location) {
                options.params.lat = this.location.latitude;
                options.params.lng = this.location.longitude;
            } else if (this.deliveryMethod === 'delivery' && this.address) {
                options.params.lat = this.address.lat;
                options.params.lng = this.address.lng;
            }
            const { data } = await this.$axios.get(`/restaurant-locations/${this.slug}`, options);
            /* if (this.coupon) {
                await this.removeCoupon();
            } */
            return data;
        },
        testDataLayer() {
            // console.log('testDataLayer method now fired')
            // Ecommerce tracking - START
            let cartItems = [];
            // this.items.forEach((item) => {
            cartItems.push({
                name: 'Test dish',
                id: 123,
                price: 99,
                brand: 'Restaurant Test',
                quantity: 1,
            });
            // });
            dataLayer.push({ event: 'event_name' });
            dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            dataLayer.push({
                ecommerce: {
                    purchase: {
                        actionField: {
                            id: 1234,
                            affiliation: 'TRIBE',
                            revenue: 120,
                        },
                        products: cartItems,
                    },
                },
            });
            window.dataLayer.push({ event: 'event_name' });
            window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
            window.dataLayer.push({
                ecommerce: {
                    purchase: {
                        actionField: {
                            id: 1234,
                            affiliation: 'TRIBE',
                            revenue: 120,
                        },
                        products: cartItems,
                    },
                },
            });
            // Ecommerce tracking - END
        },
        restaurantLocationChain() {
            if (!this.isDelivery) return;
            const restaurantLocations = [...this.restaurant.locations];
            restaurantLocations.sort((a, b) => (a.distance < b.distance ? -1 : 1));
            if (this.isDelivery) {
                restaurantLocations.sort((a, b) =>
                    a.delivery_option && b.delivery_option && a.delivery_option.time < b.delivery_option.time ? -1 : 1
                );
            }
            let restaurantLocation;
            if (this.isDelivery) {
                restaurantLocation = restaurantLocations.find(
                    x => x.delivery_option && x.delivery_option.inside_radius
                );
            } else {
                restaurantLocation = restaurantLocations.find(x => x.pickup_option);
            }
            if (!restaurantLocation) return '';
            return restaurantLocation;
        },
    },
};
